import { Button, Input, TimePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import * as React from "react";
import { Dispatch, useEffect } from "react";

import Modal from "@/ui/Modal";
import { DATE_FORMATS, getFormattedDate } from "@/utils/dates/getFormattedDate";
import { getNextRoundedTime } from "@/utils/dates/getNextRoundedTime";

import { QuickCalendar } from "../QuickCalendar";
import { disabledTime } from "./disabledTime";

type Props = {
  isPacket: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  isSubmitting?: boolean;
  onScheduleAtChange: Dispatch<React.SetStateAction<string | null>>;
  scheduleAt: string | null;
};

function ScheduleSendPrompt({
  isPacket,
  onConfirm,
  onClose,
  isSubmitting,
  onScheduleAtChange,
  scheduleAt,
}: Props): React.ReactNode {
  const currentMomentDate = moment();
  const currentMomentTime = getNextRoundedTime();

  const addTime = currentMomentDate
    .hour(currentMomentTime.hour())
    .minute(currentMomentTime.minute());

  let currentDate = moment(currentMomentDate).format("MM/DD/YYYY");
  if (scheduleAt) {
    currentDate = moment(scheduleAt, "YYYY-MM-DDTHH:mm:ss").format(
      "MM/DD/YYYY",
    );
  }

  const currentTime = moment(addTime).format("HH:mm A");

  const handleDateChange = (value: moment.Moment) => {
    onScheduleAtChange((old) => {
      const date = getFormattedDate(value, {
        format: DATE_FORMATS.ISO_DATETIME,
      });
      if (!old) return date;

      const oldDate = moment(old);
      return getFormattedDate(
        value.hour(oldDate.hour()).minute(oldDate.minute()),
        { format: DATE_FORMATS.ISO_DATETIME },
      );
    });
  };
  const handleTimeChange = (value: dayjs.Dayjs | null) => {
    onScheduleAtChange((old) => {
      if (!old || !value) return old;

      const oldDate = moment(old);
      return getFormattedDate(
        oldDate.hour(value.hour()).minute(value.minute()),
        { format: DATE_FORMATS.ISO_DATETIME },
      );
    });
  };

  const handleSend = async () => {
    onClose();
    await onConfirm();
  };

  const handleCancel = () => {
    onClose();
    onScheduleAtChange(null);
  };

  useEffect(() => {
    const formatDate = getFormattedDate(addTime, {
      format: DATE_FORMATS.ISO_DATETIME,
    });
    onScheduleAtChange(formatDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Modal
      centered
      open
      footer={() => undefined}
      onCancel={handleCancel}
      title="Schedule Send"
      width="600px"
      zIndex={9999}
    >
      <p className="text-base text-gray-500">
        Would you like to schedule this {isPacket ? "packet" : "form"} to be
        sent at a later date?
      </p>
      <div className="grid grid-cols-[266px_1fr] gap-6 w-full mb-4 mt-4">
        <QuickCalendar onSelectDate={handleDateChange} />
        <div className="flex flex-col gap-6 w-full pt-3">
          <Input
            className="border-gray-400"
            placeholder="mm/dd/yyyy"
            value={currentDate}
          />
          <TimePicker
            className="border-gray-400"
            defaultValue={dayjs(currentTime, "hh:mm")}
            disabledTime={disabledTime}
            format="hh:mm A"
            onChange={handleTimeChange}
          />
        </div>
      </div>
      <div className="flex gap-4 justify-end">
        <Button htmlType="button" onClick={handleCancel} type="default">
          Cancel
        </Button>
        <Button disabled={isSubmitting} onClick={handleSend} type="primary">
          Schedule
        </Button>
      </div>
    </Modal>
  );
}

export default ScheduleSendPrompt;
